import { useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { ChunkErrorHandler } from './chunk-errors'
import type { UpdateManager } from './update-manager'

/**
 * Domain component (BRAND_DESIGN §8/§9). Only shown when the update cannot happen silently:
 * a dirty form / active upload is blocking, the automatic reload already failed once, a draft could not
 * be saved, or a chunk failed to load. Never promises that a draft was kept unless it was.
 */
export function VersionNotice({ manager, chunk }: { manager: UpdateManager; chunk: ChunkErrorHandler }) {
  const { t } = useTranslation()
  const state = useSyncExternalStore(manager.subscribe, manager.getState, manager.getState)

  let body: { text: string; hint?: string; actions: { label: string; onClick: () => void; secondary?: boolean }[]; live: 'status' | 'alert' } | null = null

  if (state.phase === 'applying') {
    body = { text: t('pwa:update.applying'), actions: [], live: 'status' }
  } else if (state.phase === 'save_failed') {
    body = {
      text: t('pwa:update.saveFailedBody'),
      live: 'alert',
      actions: [
        { label: t('pwa:update.anyway'), onClick: () => void manager.applyNow(true) },
        { label: t('pwa:update.keepEditing'), onClick: manager.dismissSaveFailure, secondary: true },
      ],
    }
  } else if (state.phase === 'available' && (state.blocker || state.manualOnly)) {
    const hint = state.blocker === 'upload' ? t('pwa:update.hintUpload') : state.blocker === 'form' ? t('pwa:update.hintForm') : undefined
    body = {
      text: state.manualOnly && !state.blocker ? t('pwa:update.manual') : t('pwa:update.available'),
      ...(hint ? { hint } : {}),
      live: 'status',
      actions: [{ label: state.manualOnly && !state.blocker ? t('pwa:update.manualButton') : t('pwa:update.now'), onClick: () => void manager.applyNow() }],
    }
  } else if (state.chunkError) {
    body = {
      text: t('pwa:update.chunkFailed'),
      live: 'alert',
      actions: [{ label: t('pwa:update.retry'), onClick: () => void chunk.retry() }],
    }
  }
  if (!body) return null

  return (
    <div
      data-testid="version-notice"
      role={body.live}
      className="fixed inset-x-0 bottom-0 z-[60] flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]"
    >
      <div className="flex w-full max-w-form flex-col gap-3 rounded-card border border-info bg-surface-raised p-4 shadow-float sm:flex-row sm:items-center">
        <RefreshCw aria-hidden className="size-5 shrink-0 text-info" />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium text-fg">{body.text}</p>
          {body.hint ? <p className="text-small text-fg-muted">{body.hint}</p> : null}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          {body.actions.map((a) => (
            <Button key={a.label} variant={a.secondary ? 'secondary' : 'primary'} onClick={a.onClick}>
              {a.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}
